/** E-tickets: citations and written warnings, logged as the deputy writes them. */
import { useEffect, useState } from 'react';
import { RACES, TICKET_EYES, TICKET_HAIR, TICKET_KIND_LABEL, TICKET_VEHICLE_TYPES, US_STATES, type TicketKind } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { chargeWords, StatutePicker } from '../shared/StatutePicker.tsx';
import { LicenseScanButton } from '../shared/LicenseScan.tsx';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, day, EmptyState, KPI, when } from './common.tsx';

const SCDMV_NOTE = 'Write the ticket as you do today and log it here. Sending tickets electronically to the court and SCDMV turns on once SCDMV approves it for your agency.';
const vtext = (v: any) => `${v.description}${v.speed ? ` (${v.speed} in a ${v.posted ?? '?'})` : ''}`;

export function TicketsPage() {
  const { navigate } = useRouter();
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [mine, setMine] = useState(true);
  const [kind, setKind] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  const r = useLoad(() => api.get<any>(`/api/tickets?q=${encodeURIComponent(query)}&mine=${mine ? 1 : 0}&kind=${kind}`), [query, mine, kind]);
  const c = r.data?.counts;
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Records"
        title="E-tickets"
        sub="Citations and written warnings."
        actions={
          <button className="btn primary" onClick={() => navigate(at('/tickets/new'))}>
            Log a ticket
          </button>
        }
      />
      <Notice tone="info">{SCDMV_NOTE}</Notice>
      {c && (
        <div className="ops-kpis">
          <KPI n={c.citations} label="Citations, last 30 days" on={kind === 'citation'} onClick={() => setKind(kind === 'citation' ? '' : 'citation')} />
          <KPI n={c.warnings} label="Warnings, last 30 days" on={kind === 'warning'} onClick={() => setKind(kind === 'warning' ? '' : 'warning')} />
        </div>
      )}
      <div className="row between wrap gap">
        {r.data?.canSeeAll && (
          <div className="seg-ctl" role="tablist" aria-label="Whose tickets">
            <button type="button" role="tab" aria-selected={mine} className={mine ? 'on' : ''} onClick={() => setMine(true)}>
              My tickets
            </button>
            <button type="button" role="tab" aria-selected={!mine} className={!mine ? 'on' : ''} onClick={() => setMine(false)}>
              All
            </button>
          </div>
        )}
        <input type="search" className="rp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ticket #, name, tag, license #, place…" aria-label="Search tickets" />
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.tickets.length ? (
        <EmptyState icon="pen" title="No tickets yet" />
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Ticket #</th>
                <th>Written</th>
                <th>Kind</th>
                <th>Driver</th>
                <th>Violation</th>
                <th>Where</th>
                <th>Deputy</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {r.data.tickets.map((t: any) => (
                <tr key={t.id} className="clickable" onClick={() => navigate(at(`/tickets/${t.id}`))}>
                  <td className="mono">{t.ticket_number ?? '—'}</td>
                  <td>{when(t.issued_at)}</td>
                  <td>{TICKET_KIND_LABEL[t.kind as TicketKind]}</td>
                  <td>{t.person_name}</td>
                  <td>
                    {vtext(t.violations[0])}
                    {t.violations.length > 1 ? ` +${t.violations.length - 1}` : ''}
                  </td>
                  <td>{t.location}</td>
                  <td>{t.deputy_name}</td>
                  <td>{t.status === 'voided' && <Badge tone="neutral">Voided</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const nowLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};
type V = { statute: string; description: string; speed: string; posted: string };
const blankV = (): V => ({ statute: '', description: '', speed: '', posted: '' });

export function NewTicket() {
  const { navigate } = useRouter();
  const [kind, setKind] = useState<TicketKind>('citation');
  const [f, setF] = useState<Record<string, string>>({ ticketNumber: '', issuedAt: nowLocal(), location: '', courtName: '', courtDate: '', caseNumber: '', notes: '' });
  const [p, setP] = useState<Record<string, string>>({ last: '', first: '', middle: '', dob: '', sex: '', race: '', height: '', weight: '', hair: '', eyes: '', dl: '', dlState: 'SC', dlClass: '', cdl: '', street: '', city: '', state: 'SC', zip: '' });
  const [v, setV] = useState<Record<string, string>>({ plate: '', plateState: 'SC', year: '', make: '', model: '', color: '' });
  const [vtypes, setVtypes] = useState<string[]>(['auto']);
  const [own, setOwn] = useState<Record<string, any>>({ sameAsDriver: true, first: '', middle: '', last: '', street: '', city: '', state: 'SC', zip: '' });
  const [x, setX] = useState<Record<string, any>>({ cityCountyOf: '', incidentNumber: '', agencyTroop: '', courtStreet: '', courtCity: '', courtState: 'SC', courtZip: '', courtTime: '', appearanceRequired: null, points: '', baLevel: '', county: '', city: '', officerRank: '', sccja: '', bailDeposited: '', arrestDate: '', bondRequested: '' });
  useEffect(() => {
    api.get<any>('/api/tickets/defaults').then((d) => setX((cur) => ({ ...cur, officerRank: cur.officerRank || d.officerRank || '', sccja: cur.sccja || d.sccja || '', agencyTroop: cur.agencyTroop || d.agencyTroop || '', county: cur.county || d.county || '', cityCountyOf: cur.cityCountyOf || d.county || '' })), () => undefined);
  }, []);
  const sx = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setX({ ...x, [k]: e.target.value });
  const so = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setOwn({ ...own, [k]: e.target.value });
  const numOr = (s: string) => (s === '' || s == null ? undefined : Number(s));
  const [viol, setViol] = useState<V[]>([blankV()]);
  const a = useAction();
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  const sp = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setP({ ...p, [k]: e.target.value });
  const sv = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setV({ ...v, [k]: e.target.value });
  const setVi = (i: number, patch: Partial<V>) => setViol(viol.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <div className="ops stack">
      <PageHeader crumb="E-tickets" title="Log a ticket" />
      <Notice tone="info">{SCDMV_NOTE}</Notice>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => {
            const r = await api.post<{ id: string }>('/api/tickets', {
              kind,
              ...f,
              issuedAt: new Date(f.issuedAt!).toISOString(),
              courtDate: f.courtDate || undefined,
              person: { ...p, cdl: p.cdl === 'yes' ? true : p.cdl === 'no' ? false : undefined },
              vehicle: { ...v, types: vtypes, owner: own },
              extra: {
                ...x,
                appearanceRequired: x.appearanceRequired ?? undefined,
                points: numOr(x.points),
                bailDeposited: numOr(x.bailDeposited),
                bondRequested: numOr(x.bondRequested),
                arrestDate: x.arrestDate || undefined,
                courtTime: x.courtTime || undefined,
                baLevel: x.baLevel || undefined,
              },
              violations: viol.filter((x) => x.description.trim()).map((x) => ({ statute: x.statute || undefined, description: x.description, speed: x.speed ? Number(x.speed) : undefined, posted: x.posted ? Number(x.posted) : undefined })),
            });
            navigate(at(`/tickets/${r.id}`), true);
          });
        }}
      >
        <Card title="Ticket">
          <div className="seg-ctl" role="radiogroup" aria-label="Citation or warning">
            {(['citation', 'warning'] as const).map((k) => (
              <button key={k} type="button" role="radio" aria-checked={kind === k} className={kind === k ? 'on' : ''} onClick={() => setKind(k)}>
                {TICKET_KIND_LABEL[k]}
              </button>
            ))}
          </div>
          <div className="grid-3">
            <label>
              {kind === 'citation' ? 'Ticket number' : 'Warning number (if any)'}
              <input value={f.ticketNumber} onChange={set('ticketNumber')} required={kind === 'citation'} className="mono" />
            </label>
            <label>
              Written at
              <input type="datetime-local" value={f.issuedAt} onChange={set('issuedAt')} required />
            </label>
            <label>
              Case # (if there’s a report)
              <input value={f.caseNumber} onChange={set('caseNumber')} />
            </label>
          </div>
          <label>
            Location
            <input value={f.location} onChange={set('location')} required placeholder="Road and mile marker, or address" />
          </label>
        </Card>
        <Card title="Driver" actions={<LicenseScanButton small onScan={(d) => setP({ ...p, last: d.last ?? '', first: d.first ?? '', middle: d.middle ?? '', dob: d.dob ?? '', sex: d.sex ?? '', height: d.height ?? '', weight: d.weight ? String(d.weight) : '', hair: d.hair ?? '', eyes: d.eyes ?? '', dl: d.dl ?? '', dlState: d.dlState ?? 'SC', dlClass: d.dlClass ?? '', street: d.street ?? '', city: d.city ?? '', state: d.state ?? 'SC', zip: d.zip ?? '' })} />}>
          <div className="grid-4">
            <label>
              Last name
              <input value={p.last} onChange={sp('last')} required />
            </label>
            <label>
              First name
              <input value={p.first} onChange={sp('first')} required />
            </label>
            <label>
              Middle
              <input value={p.middle} onChange={sp('middle')} />
            </label>
            <label>
              Date of birth
              <input type="date" value={p.dob} onChange={sp('dob')} />
            </label>
            <label>
              Driver’s license #
              <input value={p.dl} onChange={sp('dl')} className="mono" />
            </label>
            <label>
              DL state
              <select value={p.dlState} onChange={sp('dlState')}>
                {US_STATES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            <label>
              Class
              <input value={p.dlClass} onChange={sp('dlClass')} maxLength={3} placeholder="D" />
            </label>
            <label>
              CDL
              <select value={p.cdl} onChange={sp('cdl')}>
                <option value="">—</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
            <label>
              Race
              <select value={p.race} onChange={sp('race')}>
                <option value="">—</option>
                {RACES.map((r) => (
                  <option key={r.code} value={r.code}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Sex
              <select value={p.sex} onChange={sp('sex')}>
                <option value="">—</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
              </select>
            </label>
            <label>
              Height
              <input value={p.height} onChange={sp('height')} placeholder="5-10" />
            </label>
            <label>
              Weight
              <input value={p.weight} onChange={sp('weight')} inputMode="numeric" />
            </label>
            <label>
              Hair
              <select value={p.hair} onChange={sp('hair')}>
                <option value="">—</option>
                {TICKET_HAIR.map((h) => (
                  <option key={h}>{h}</option>
                ))}
              </select>
            </label>
            <label>
              Eyes
              <select value={p.eyes} onChange={sp('eyes')}>
                <option value="">—</option>
                {TICKET_EYES.map((h) => (
                  <option key={h}>{h}</option>
                ))}
              </select>
            </label>
            <label style={{ gridColumn: 'span 2' }}>
              Street
              <input value={p.street} onChange={sp('street')} />
            </label>
            <label>
              City
              <input value={p.city} onChange={sp('city')} />
            </label>
            <span className="grid-2" style={{ gap: 8 }}>
              <label>
                State
                <select value={p.state} onChange={sp('state')}>
                  {US_STATES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label>
                Zip
                <input value={p.zip} onChange={sp('zip')} inputMode="numeric" />
              </label>
            </span>
          </div>
        </Card>
        <Card title="Vehicle">
          <div className="grid-4">
            <label>
              Tag
              <input value={v.plate} onChange={sv('plate')} className="mono" />
            </label>
            <label>
              State
              <select value={v.plateState} onChange={sv('plateState')}>
                {US_STATES.map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </label>
            <label>
              Make
              <input value={v.make} onChange={sv('make')} />
            </label>
            <label>
              Model
              <input value={v.model} onChange={sv('model')} />
            </label>
            <label>
              Year
              <input value={v.year} onChange={sv('year')} inputMode="numeric" maxLength={4} />
            </label>
          </div>
          <div className="tk-types" role="group" aria-label="Vehicle type">
            {TICKET_VEHICLE_TYPES.map((t) => (
              <label key={t.code} className="check">
                <input type="checkbox" checked={vtypes.includes(t.code)} onChange={(e) => setVtypes(e.target.checked ? [...vtypes, t.code] : vtypes.filter((y) => y !== t.code))} /> {t.label}
              </label>
            ))}
          </div>
          <label className="check">
            <input type="checkbox" checked={own.sameAsDriver} onChange={(e) => setOwn({ ...own, sameAsDriver: e.target.checked })} /> The driver owns the vehicle
          </label>
          {!own.sameAsDriver && (
            <div className="grid-4">
              <label>
                Owner last name
                <input value={own.last} onChange={so('last')} />
              </label>
              <label>
                First
                <input value={own.first} onChange={so('first')} />
              </label>
              <label>
                Middle
                <input value={own.middle} onChange={so('middle')} />
              </label>
              <label>
                Owner street
                <input value={own.street} onChange={so('street')} />
              </label>
              <label>
                City
                <input value={own.city} onChange={so('city')} />
              </label>
              <label>
                State
                <select value={own.state} onChange={so('state')}>
                  {US_STATES.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label>
                Zip
                <input value={own.zip} onChange={so('zip')} />
              </label>
            </div>
          )}
        </Card>
        <Card title="Violations">
          {viol.map((x, i) => (
            <div key={i} className="grid-4">
              <label>
                Statute
                <input value={x.statute} onChange={(e) => setVi(i, { statute: e.target.value })} className="mono" placeholder="56-5-1520" />
              </label>
              <label style={{ gridColumn: 'span 3' }}>
                Violation
                <StatutePicker
                  value={x.description}
                  label="Violation"
                  placeholder="Type the violation or statute (speeding, 56-5-1520)"
                  onText={(v) => setVi(i, { description: v })}
                  onPick={(p) => setVi(i, { description: chargeWords(p.description), statute: p.statute })}
                />
              </label>
              {/speed/i.test(x.description) && (
                <>
                  <label>
                    Speed
                    <input value={x.speed} onChange={(e) => setVi(i, { speed: e.target.value })} inputMode="numeric" />
                  </label>
                  <label>
                    Posted limit
                    <input value={x.posted} onChange={(e) => setVi(i, { posted: e.target.value })} inputMode="numeric" />
                  </label>
                </>
              )}
            </div>
          ))}
          {viol.length < 10 && (
            <button type="button" className="btn ghost" onClick={() => setViol([...viol, blankV()])}>
              + Another violation
            </button>
          )}
        </Card>
        <Card title="Where and the rest of the violation block">
          <div className="grid-4">
            <label>
              County
              <input value={x.county} onChange={sx('county')} />
            </label>
            <label>
              City (if inside one)
              <input value={x.city} onChange={sx('city')} />
            </label>
            <label>
              SC points
              <input value={x.points} onChange={sx('points')} inputMode="numeric" />
            </label>
            <label>
              B.A. level (DUI)
              <input value={x.baLevel} onChange={sx('baLevel')} placeholder=".08" />
            </label>
            <label>
              City/County of
              <input value={x.cityCountyOf} onChange={sx('cityCountyOf')} />
            </label>
            <label>
              Incident no.
              <input value={x.incidentNumber} onChange={sx('incidentNumber')} className="mono" />
            </label>
            <label>
              Agency / troop
              <input value={x.agencyTroop} onChange={sx('agencyTroop')} />
            </label>
          </div>
        </Card>
        {kind === 'citation' && (
          <Card title="Court">
            <div className="grid-4">
              <label style={{ gridColumn: 'span 2' }}>
                Name of trial court
                <input value={f.courtName} onChange={set('courtName')} placeholder="Magistrate court" />
              </label>
              <label>
                Date of trial
                <input type="date" value={f.courtDate} onChange={set('courtDate')} />
              </label>
              <label>
                Time of trial
                <input type="time" value={x.courtTime} onChange={sx('courtTime')} />
              </label>
              <label style={{ gridColumn: 'span 2' }}>
                Court street
                <input value={x.courtStreet} onChange={sx('courtStreet')} />
              </label>
              <label>
                City
                <input value={x.courtCity} onChange={sx('courtCity')} />
              </label>
              <label>
                Zip
                <input value={x.courtZip} onChange={sx('courtZip')} />
              </label>
            </div>
            <div className="row gap wrap" role="radiogroup" aria-label="Court appearance required">
              <span className="small strong">Court appearance required?</span>
              <label className="check">
                <input type="radio" name="appear" checked={x.appearanceRequired === true} onChange={() => setX({ ...x, appearanceRequired: true })} /> Yes
              </label>
              <label className="check">
                <input type="radio" name="appear" checked={x.appearanceRequired === false} onChange={() => setX({ ...x, appearanceRequired: false })} /> No
              </label>
            </div>
          </Card>
        )}
        <Card title="Officer and arrest">
          <div className="grid-4">
            <label>
              Your rank
              <input value={x.officerRank} onChange={sx('officerRank')} />
            </label>
            <label>
              SCCJA officer #
              <input value={x.sccja} onChange={sx('sccja')} className="mono" />
            </label>
            <label>
              Date of arrest
              <input type="date" value={x.arrestDate} onChange={sx('arrestDate')} />
            </label>
            <label>
              Bail deposited ($)
              <input value={x.bailDeposited} onChange={sx('bailDeposited')} inputMode="decimal" />
            </label>
            <label>
              Bond amount requested ($)
              <input value={x.bondRequested} onChange={sx('bondRequested')} inputMode="decimal" />
            </label>
          </div>
        </Card>
        <label>
          Notes
          <textarea rows={2} value={f.notes} onChange={set('notes')} />
        </label>
        <ErrorBox error={a.error} />
        <div className="row gap">
          <button className="btn primary" disabled={a.busy}>
            {a.busy ? 'Saving…' : `Save ${kind === 'citation' ? 'citation' : 'warning'}`}
          </button>
          <button type="button" className="btn ghost" onClick={() => history.back()}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export function TicketDetail({ id }: { id: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/tickets/${id}`), [id]);
  const a = useAction();
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const t = r.data.ticket;
  return (
    <div className="ops stack">
      <PageHeader
        crumb="E-tickets"
        title={`${TICKET_KIND_LABEL[t.kind as TicketKind]}${t.ticket_number ? ` ${t.ticket_number}` : ''}`}
        sub={`${t.person_name} · ${when(t.issued_at)} · ${t.deputy_name}`}
        actions={
          <div className="row gap-sm">
            {t.status === 'voided' && <Badge tone="neutral">Voided</Badge>}
            <button className="btn" onClick={() => navigate(at(`/tickets/${id}/print`))}>
              Print
            </button>
          </div>
        }
      />
      {t.status === 'voided' && <Notice tone="warn">Voided {when(t.voided_at)}: {t.void_reason}</Notice>}
      <ErrorBox error={a.error} />
      <Card title="Violations">
        <ul>
          {t.violations.map((v: any, i: number) => (
            <li key={i}>
              {v.statute && <span className="mono">{v.statute} · </span>}
              {vtext(v)}
            </li>
          ))}
        </ul>
      </Card>
      <Card title="Details">
        <dl className="kv">
          <dt>Location</dt>
          <dd>{t.location}</dd>
          <dt>Driver</dt>
          <dd>{[t.person_name, t.person?.dob && `DOB ${day(t.person.dob)}`, t.person?.dl && `DL ${t.person.dl} ${t.person.dlState ?? ''}`, t.person?.address].filter(Boolean).join(' · ')}</dd>
          <dt>Vehicle</dt>
          <dd>{[t.vehicle?.plate && `${t.vehicle.plate} ${t.vehicle.plateState ?? ''}`, t.vehicle?.make, t.vehicle?.model].filter(Boolean).join(' · ') || '—'}</dd>
          {t.kind === 'citation' && (
            <>
              <dt>Court</dt>
              <dd>{[t.court_name, t.court_date && day(t.court_date)].filter(Boolean).join(' · ') || '—'}</dd>
            </>
          )}
          <dt>Case #</dt>
          <dd>{t.case_number ?? '—'}</dd>
          {t.notes && (
            <>
              <dt>Notes</dt>
              <dd>{t.notes}</dd>
            </>
          )}
        </dl>
      </Card>
      {r.data.canVoid && (
        <button
          className="btn ghost"
          disabled={a.busy}
          onClick={() => {
            const reason = window.prompt('Why is this ticket being voided?')?.trim();
            if (reason) a.run(async () => (await api.post(`/api/tickets/${id}/void`, { reason }), r.reload()));
          }}
        >
          Void this ticket
        </button>
      )}
    </div>
  );
}
